export type ApiErrorCode =
  | 'API_AUTH_NOT_CONFIGURED'
  | 'API_UNAUTHORIZED'
  | 'API_FORBIDDEN'
  | 'API_NOT_FOUND'
  | 'API_NOT_CONFIGURED'
  | 'API_SERVER_ERROR'
  | 'API_REQUEST_FAILED'

export class ApiError extends Error {
  constructor(
    public code: ApiErrorCode,
    message: string,
    public status?: number,
    public correlationId?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiClientOptions {
  baseUrl?: string
  getAccessToken?: () => Promise<string>
}

export interface ApiRequestOptions {
  signal?: AbortSignal
  auth?: boolean
  idempotencyKey?: string
}

export const WACNER_ACCESS_TOKEN_HEADER = 'X-Wacner-Access-Token'

export class ApiClient {
  private baseUrl: string
  private getAccessToken?: () => Promise<string>

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? ''
    this.getAccessToken = options.getAccessToken
  }

  async get<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(path, { method: 'GET', ...options })
  }

  async post<T>(path: string, body: unknown, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(path, { method: 'POST', body, ...options })
  }

  /** A lost response keeps the same key across reloads; no request contents are stored. */
  async postOnce<T>(path: string, body: unknown): Promise<T> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([path, body])))
    const storageKey = `sales:request:${Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')}`
    const key = sessionStorage.getItem(storageKey) || crypto.randomUUID()
    // Fail before writing if durable browser storage is unavailable.
    sessionStorage.setItem(storageKey, key)
    const result = await this.post<T>(path, body, { idempotencyKey: key })
    sessionStorage.removeItem(storageKey)
    return result
  }

  async patch<T>(path: string, body: unknown, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body, ...options })
  }

  async delete<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', ...options })
  }

  private async request<T>(
    path: string,
    options: ApiRequestOptions & { method: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown }
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    }

    if (options.auth !== false) {
      if (!this.getAccessToken) {
        throw new ApiError('API_AUTH_NOT_CONFIGURED', 'API access token provider is not configured')
      }
      const token = await this.getAccessToken()
      headers[WACNER_ACCESS_TOKEN_HEADER] = `Bearer ${token}`
    }

    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

    const hasBody = options.body !== undefined
    if (hasBody) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: options.method,
      headers,
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    })

    if (!response.ok) {
      throw await this.toApiError(response)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return response.json() as Promise<T>
  }

  private async toApiError(response: Response): Promise<ApiError> {
    const raw = await response.text().catch(() => '')
    // Some endpoints (api/src/copilot.js) attach a correlationId to their
    // JSON error body for support/debugging — surfaced here so the UI can
    // show it without ever showing the rest of the raw response.
    let message = raw
    let correlationId: string | undefined
    try {
      const parsed = raw ? JSON.parse(raw) : null
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.error === 'string') message = parsed.error
        if (typeof parsed.correlationId === 'string') correlationId = parsed.correlationId
      }
    } catch {
      // Not JSON — fall back to the raw text as the message.
    }

    if (response.status === 401) {
      return new ApiError('API_UNAUTHORIZED', message || 'Unauthorized', response.status, correlationId)
    }
    if (response.status === 403) {
      return new ApiError('API_FORBIDDEN', message || 'Forbidden', response.status, correlationId)
    }
    if (response.status === 404) {
      return new ApiError('API_NOT_FOUND', message || 'Not found', response.status, correlationId)
    }
    if (response.status === 503) {
      return new ApiError('API_NOT_CONFIGURED', message || 'Not configured', response.status, correlationId)
    }
    if (response.status >= 500) {
      return new ApiError('API_SERVER_ERROR', message || 'Server error', response.status, correlationId)
    }

    return new ApiError('API_REQUEST_FAILED', message || 'Request failed', response.status, correlationId)
  }
}
